import { HorizontalSpaceBetween, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import DonutChart from 'components/DonutChart';
import CautionStatusBadge from 'components/badges/CautionStatusBadge';
import GoodStatusBadge from 'components/badges/GoodStatusBadge';
import BodyText from 'components/text/BodyText';
import CaptionText from 'components/text/CaptionText';

import useTranslation from './hooks/useTranslation';

import { AGING_THRESHOLD, CYCLE_LIFE_COLOR_MAP, FULL_PERCENT, TEST_ID } from './constants';
import type { BatteryStatus, FunctionComponent, Props, StatusBadgeComponent } from './types';

import styles from './styles.module.scss';

const statusBadgeMap: Record<BatteryStatus, StatusBadgeComponent> = {
    AGING: CautionStatusBadge,
    OK: GoodStatusBadge
};

const BatteryCard: FunctionComponent<Props> = ({
    className = '',
    cyclesTotal,
    cyclesUsed,
    dronesUsedWith,
    isSyncStale = false,
    lastSynced,
    name,
    testId = TEST_ID
}) => {
    const { AGING, CYCLE_LIFE, CYCLES, CYCLES_VALUE, OK, SYNCED, USED_WITH } = useTranslation();

    const cycleLife =
        cyclesTotal === 0 ? 0 : Math.round((1 - cyclesUsed / cyclesTotal) * FULL_PERCENT);
    const status: BatteryStatus = cycleLife < AGING_THRESHOLD ? 'AGING' : 'OK';
    const StatusBadge = statusBadgeMap[status];
    const syncedClass = isSyncStale ? styles.syncedStale : '';

    return (
        <Wrapper className={`${className} ${styles.batteryCard}`} testId={testId}>
            <HorizontalSpaceBetween className={styles.header}>
                <BodyText className={styles.name} text={name} />

                <StatusBadge text={status === 'AGING' ? AGING : OK} />
            </HorizontalSpaceBetween>

            <CaptionText className={`${styles.synced} ${syncedClass}`} text={SYNCED(lastSynced)} />

            <DonutChart
                className={styles.donut}
                colorMap={CYCLE_LIFE_COLOR_MAP}
                label={CYCLE_LIFE}
                percent={cycleLife}
            />

            <VerticalContainer className={styles.metrics}>
                <HorizontalSpaceBetween className={styles.metricRow}>
                    <CaptionText className={styles.metricLabel} text={CYCLES} />

                    <BodyText
                        className={styles.metricValue}
                        text={CYCLES_VALUE(cyclesUsed, cyclesTotal)}
                    />
                </HorizontalSpaceBetween>

                <HorizontalSpaceBetween className={styles.metricRow}>
                    <CaptionText className={styles.metricLabel} text={USED_WITH} />

                    <BodyText className={styles.metricValue} text={dronesUsedWith.join(', ')} />
                </HorizontalSpaceBetween>
            </VerticalContainer>
        </Wrapper>
    );
};

export default BatteryCard;
